import { api } from './api';

export type ProposalKind =
  | 'create_task'
  | 'assign_task'
  | 'set_due_date'
  | 'add_comment'
  | 'add_project_member'
  | 'create_employee'
  | 'create_widget'
  | 'request_absence'
  | 'log_time'
  | 'create_project';

export interface Proposal {
  kind: ProposalKind;
  /** Set when this same change was already proposed earlier in the conversation. */
  repeat?: boolean;
  /** Human sentence rendered on the confirmation card. */
  summary: string;
  /** Every field the change will write, when it is worth reviewing in full. */
  details?: { label: string; value: string }[];
  payload: Record<string, unknown>;
}

export interface AssistantReply {
  text: string;
  /** Changes the assistant prepared. Nothing has been applied yet. */
  proposals: Proposal[];
  /** Tool names the model called, shown as "what I checked". */
  toolsUsed: string[];
  /** Where this turn was recorded. Send it back to keep the same thread. */
  conversationId?: string;
  /** What the read tools returned, for the renderers to draw. */
  data?: AnswerData[];
}

/** One tool result, tagged with the tool that produced it. */
export interface AnswerData {
  tool: string;
  result: unknown;
}

export interface CapabilityGroup {
  domain: string;
  label: string;
  items: { phrase: string; example: string; writes: boolean }[];
}

/** What this user's assistant can do, grouped for the command palette. */
export async function getCapabilities(): Promise<{ groups: CapabilityGroup[] }> {
  const { data } = await api.get<{ groups: CapabilityGroup[] }>('/ai/assistant/capabilities');
  return data;
}

export interface ConversationSummary {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  messageCount: number;
}

export interface ConversationMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  proposals: Proposal[] | null;
  toolsUsed: string[];
  createdAt: string;
}

export interface Conversation {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  messages: ConversationMessage[];
}

/** Your own past conversations, newest first. */
export async function listConversations(): Promise<ConversationSummary[]> {
  const { data } = await api.get<ConversationSummary[]>('/ai/assistant/conversations');
  return data;
}

export async function getConversation(id: string): Promise<Conversation> {
  const { data } = await api.get<Conversation>(`/ai/assistant/conversations/${id}`);
  return data;
}

export async function deleteConversation(id: string): Promise<void> {
  await api.delete(`/ai/assistant/conversations/${id}`);
}

export interface AssistantMessage {
  role: 'user' | 'assistant';
  content: string;
}

/**
 * Today's briefing. The instruction lives on the server so a scheduled run and
 * this button can never drift apart.
 */
export async function getBriefing(): Promise<AssistantReply> {
  const { data } = await api.post<AssistantReply>('/ai/assistant/briefing', {});
  return data;
}

export async function askAssistant(
  messages: AssistantMessage[],
  context?: Record<string, string>,
  conversationId?: string,
): Promise<AssistantReply> {
  const { data } = await api.post<AssistantReply>('/ai/assistant', {
    module: 'assistant',
    messages,
    ...(context && { context }),
    ...(conversationId && { conversationId }),
  });
  return data;
}
